import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Truck, Send } from "lucide-react";
import { toast } from "sonner";
import { api, abs, money, errMsg } from "../../lib/api";
import { Loading } from "../../components/PageHeader";
import { AdminTitle, Pill, ORDER_TONE, ErrorBox } from "./AdminLayout";

function ShipPanel({ o, setO }) {
  const [note, setNote] = useState(o.tracking_note || "");
  const [busy, setBusy] = useState(false);
  const ship = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/ship`, { tracking_note: note });
      setO(data); toast.success("Order marked as shipped — buyer notified");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="wb-card p-6" data-testid="ship-panel">
      <div className="eyebrow mb-4">Fulfilment</div>
      {o.shipped_at && <p className="mb-4 text-sm muted" data-testid="shipped-at">Shipped {new Date(o.shipped_at).toLocaleString()}</p>}
      <label className="wb-label">Tracking number / note (optional)</label>
      <textarea rows={3} className="wb-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="tracking-note-input" />
      <button onClick={ship} disabled={busy || !["paid", "shipped"].includes(o.status)} className="btn-gold mt-4" data-testid="mark-shipped-button">
        <Truck size={14} /> {o.status === "shipped" ? "Update shipping note" : "Mark as shipped"}
      </button>
    </div>
  );
}

const BuyerCard = ({ o }) => (
  <div className="wb-card p-6">
    <div className="eyebrow mb-4">Buyer</div>
    <p className="text-sm" data-testid="admin-order-email">{o.email}</p>
    <p className="mt-1 text-xs muted">{o.user_deleted ? "Deleted user" : o.user ? `Account: ${o.user.name || o.user.email}` : "Guest checkout"}</p>
  </div>
);

const AddressCard = ({ a }) => (
  <div className="wb-card p-6" data-testid="admin-order-address">
    <div className="eyebrow mb-4">Ship to</div>
    <p className="text-sm leading-relaxed">{a.name}<br />{a.line1}{a.line2 && `, ${a.line2}`}<br />{a.postal_code} {a.city}{a.state && `, ${a.state}`}<br />{a.country}{a.phone && <><br />{a.phone}</>}</p>
  </div>
);

const ItemsCard = ({ o }) => (
  <div className="wb-card p-6">
    <div className="eyebrow mb-4">Items</div>
    {o.items.map((i) => (
      <div key={i.product_id} className="flex items-center gap-4 border-b wb-line py-3 text-sm">
        {i.image_url && <img src={abs(i.image_url)} alt="" className="h-12 w-12 object-cover" />}
        <span className="flex-1">{i.title} <span className="muted">× {i.quantity}</span></span>
        <span className="font-mono">{money(i.price * i.quantity)}</span>
      </div>
    ))}
    <div className="mt-4 space-y-1 text-sm">
      <div className="flex justify-between muted"><span>Subtotal</span><span className="font-mono">{money(o.subtotal)}</span></div>
      <div className="flex justify-between muted"><span>Shipping</span><span className="font-mono">{money(o.shipping_total)}</span></div>
      <div className="flex justify-between"><span>Paid</span><span className="font-mono gold">{money(o.amount_paid ?? o.total)}</span></div>
    </div>
  </div>
);

function DigitalCard({ o, setO }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/orders/${o.id}/resend-links`); setO(data); toast.success("Fresh download links emailed to buyer"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="wb-card p-6" data-testid="admin-order-digital">
      <div className="eyebrow mb-4">Digital delivery</div>
      {o.digital_items.map((d) => (
        <div key={d.product_id} className="flex justify-between border-b wb-line py-2 text-sm">
          <span>{d.title}</span>
          <span className="font-mono text-xs muted">{d.expires_at ? `expires ${new Date(d.expires_at).toLocaleDateString()}` : "expired"}</span>
        </div>
      ))}
      <button onClick={resend} disabled={busy} className="btn-ghost mt-5" data-testid="admin-resend-links-button"><Send size={14} /> {busy ? "Sending…" : "Regenerate & resend links"}</button>
    </div>
  );
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/admin/orders/${id}`).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  if (err) return <ErrorBox msg={err} />;
  if (!o) return <Loading />;
  const physical = o.items.some((i) => i.type === "physical_art");
  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin/orders" className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] muted hover:text-wb-gold" data-testid="back-to-orders"><ArrowLeft size={12} /> Orders</Link>
      <AdminTitle eyebrow={new Date(o.created_at).toLocaleString()} title={o.order_number}>
        <Pill tone={ORDER_TONE[o.status]} testid="admin-order-status">{o.status}</Pill>
      </AdminTitle>
      {o.stock_issues?.length > 0 && <div className="mb-6"><ErrorBox msg={o.stock_issues.join("; ")} /></div>}
      <div className="grid gap-8 xl:grid-cols-2">
        <div className="space-y-8">
          <BuyerCard o={o} />
          {o.shipping_address && <AddressCard a={o.shipping_address} />}
          {physical && <ShipPanel o={o} setO={setO} />}
        </div>
        <div className="space-y-8">
          <ItemsCard o={o} />
          {o.digital_items?.length > 0 && <DigitalCard o={o} setO={setO} />}
        </div>
      </div>
    </div>
  );
}
